import { useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, CheckCircle, ExternalLink, Trash2 } from 'lucide-react';
import { useFormSubmissions, useDeleteSubmission } from '@/hooks/useForms';
import { Card, EmptyState, PageHeader, Select, Skeleton } from '@solid/ui';
import { Badge } from '@/components/ui/Badge';
import type { AiReviewStatus } from '@/types';

const statusOptions = [
  { value: '', label: 'All Statuses' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'REVIEWING', label: 'Reviewing' },
  { value: 'COMPLETE', label: 'Complete' },
  { value: 'FOLLOW_UP_SENT', label: 'Follow-up Sent' },
  { value: 'ERROR', label: 'Error' },
];

const typeOptions = [
  { value: '', label: 'All Types' },
  { value: 'CONTACT', label: 'Contact' },
  { value: 'RFP', label: 'RFP' },
];

const processedOptions = [
  { value: '', label: 'All' },
  { value: 'false', label: 'Unprocessed' },
  { value: 'true', label: 'Processed' },
];

const statusBadge: Record<AiReviewStatus, { variant: 'default' | 'primary' | 'success' | 'warning' | 'error'; label: string }> = {
  PENDING: { variant: 'default', label: 'Pending' },
  REVIEWING: { variant: 'primary', label: 'Reviewing' },
  COMPLETE: { variant: 'success', label: 'Complete' },
  FOLLOW_UP_SENT: { variant: 'warning', label: 'Follow-up Sent' },
  ERROR: { variant: 'error', label: 'Error' },
};

export default function FormSubmissions() {
  const [formType, setFormType] = useState('');
  const [aiReviewStatus, setAiReviewStatus] = useState('');
  const [processed, setProcessed] = useState('');
  const deleteMutation = useDeleteSubmission();

  const handleDelete = (e: React.MouseEvent, id: string, name: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (window.confirm(`Delete submission from "${name}"? This cannot be undone.`)) {
      deleteMutation.mutate(id);
    }
  };

  const { data, isLoading } = useFormSubmissions({
    formType: formType || undefined,
    aiReviewStatus: aiReviewStatus || undefined,
    processed: processed || undefined,
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Form Submissions"
        subtitle="RFP and contact form submissions with AI review status"
      />

      <div className="flex items-center gap-3">
        <Select
          options={typeOptions}
          value={formType}
          onChange={(e) => setFormType(e.target.value)}
        />
        <Select
          options={statusOptions}
          value={aiReviewStatus}
          onChange={(e) => setAiReviewStatus(e.target.value)}
        />
        <Select
          options={processedOptions}
          value={processed}
          onChange={(e) => setProcessed(e.target.value)}
        />
      </div>

      <Card padding="md">
        {isLoading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        ) : !data?.data.length ? (
          <EmptyState icon={<FileText />} title="No form submissions yet" />
        ) : (
          <div className="space-y-2">
            {data.data.map((submission) => {
              const badge = statusBadge[submission.aiReviewStatus];
              const formData = submission.formData || {};
              const contactName = submission.contact
                ? `${submission.contact.firstName} ${submission.contact.lastName}`
                : formData.firstName
                  ? `${formData.firstName} ${formData.lastName || ''}`
                  : formData.email || 'Unknown';
              const score = submission.aiReviewResult?.qualificationScore;

              return (
                <Link
                  key={submission.id}
                  to={`/admin/forms/${submission.id}`}
                  className="flex items-center gap-4 p-4 rounded-lg bg-grey-50 hover:bg-grey-100 transition-colors"
                >
                  <div className="flex-shrink-0">
                    {submission.formType === 'RFP' ? (
                      <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-accent" />
                      </div>
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-grey-200 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-grey-500" />
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-secondary truncate">
                        {contactName}
                      </span>
                      <Badge variant={submission.formType === 'RFP' ? 'primary' : 'default'} size="sm">
                        {submission.formType}
                      </Badge>
                    </div>
                    <p className="text-xs text-grey-500 mt-0.5 truncate">
                      {formData.email || formData.company || submission.sourceUrl || 'No details'}
                    </p>
                  </div>

                  {score !== undefined && (
                    <div className="flex-shrink-0 text-center">
                      <div
                        className={`text-lg font-bold ${
                          score >= 70 ? 'text-success' : score >= 40 ? 'text-warning' : 'text-error'
                        }`}
                      >
                        {score}
                      </div>
                      <div className="text-[10px] text-grey-400 uppercase">Score</div>
                    </div>
                  )}

                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Badge variant={badge.variant} size="sm">
                      {badge.label}
                    </Badge>
                    {submission.processed && (
                      <CheckCircle className="w-4 h-4 text-success" />
                    )}
                  </div>

                  <div className="text-xs text-grey-400 flex-shrink-0 w-24 text-right">
                    {new Date(submission.createdAt).toLocaleDateString()}
                  </div>

                  <button
                    onClick={(e) => handleDelete(e, submission.id, contactName)}
                    className="p-1.5 rounded-md text-grey-300 hover:text-error hover:bg-error/5 transition-colors flex-shrink-0"
                    aria-label="Delete submission"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>

                  <ExternalLink className="w-4 h-4 text-grey-300 flex-shrink-0" />
                </Link>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
